import { useState, useEffect } from "react";
import { useAuth } from "@/App";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { FileText, Loader2, Pencil } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const FIELDS = [
  ["denominazione", "Denominazione / Ragione sociale", "sm:col-span-2"],
  ["indirizzo", "Indirizzo (via e numero)", "sm:col-span-2"],
  ["cap", "CAP", ""],
  ["citta", "Città", ""],
  ["provincia", "Provincia (sigla)", ""],
  ["partita_iva", "Partita IVA", ""],
  ["codice_fiscale", "Codice fiscale (se diverso)", ""],
  ["codice_sdi", "Codice destinatario SDI", ""],
  ["pec", "PEC", ""],
  ["email_fattura", "Email per l'invio della fattura", ""],
];

const EMPTY = Object.fromEntries(FIELDS.map(([k]) => [k, ""]));

export default function BillingInfoCard() {
  const { token } = useAuth();
  const [info, setInfo] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    axios.get(`${API}/api/company/billing-info`, { headers })
      .then((res) => {
        const data = res.data || {};
        setInfo(data);
        setForm({ ...EMPTY, ...Object.fromEntries(Object.entries(data).filter(([k]) => k in EMPTY)) });
        setEditing(!data.denominazione);
      })
      .catch(() => setInfo({}));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  if (info === null) return null;

  const save = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/api/company/billing-info`, form, { headers });
      setInfo(res.data);
      setEditing(false);
      toast.success("Dati per la fattura salvati");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm" data-testid="billing-info-card">
      <div className="flex items-start justify-between gap-3 mb-1">
        <div className="flex items-center gap-2">
          <FileText className="h-5 w-5 text-blue-600" />
          <h2 className="text-lg font-bold text-slate-900">Dati per la fattura</h2>
        </div>
        {!editing && (
          <Button variant="outline" size="sm" onClick={() => setEditing(true)} data-testid="billing-info-edit-btn">
            <Pencil className="h-4 w-4 mr-1" /> Modifica
          </Button>
        )}
      </div>
      <p className="text-sm text-slate-500 mb-4">
        Facoltativo. Se desideri la fattura per i pagamenti, inserisci qui i dati della tua azienda: li useremo per emetterla.
      </p>

      {editing ? (
        <div className="grid sm:grid-cols-2 gap-3" data-testid="billing-info-form">
          {FIELDS.map(([key, label, span]) => (
            <div key={key} className={span}>
              <label className="text-xs font-medium text-slate-600">{label}</label>
              <Input
                value={form[key]}
                onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                data-testid={`billing-${key}-input`}
              />
            </div>
          ))}
          <div className="sm:col-span-2 flex gap-2 justify-end pt-2">
            {info.denominazione && (
              <Button variant="ghost" onClick={() => setEditing(false)} data-testid="billing-info-cancel-btn">Annulla</Button>
            )}
            <Button onClick={save} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="billing-info-save-btn">
              {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Salva dati fattura
            </Button>
          </div>
        </div>
      ) : (
        <div className="text-sm text-slate-700 space-y-0.5" data-testid="billing-info-summary">
          <p className="font-semibold">{info.denominazione}</p>
          <p>{[info.indirizzo, [info.cap, info.citta].filter(Boolean).join(" "), info.provincia].filter(Boolean).join(", ")}</p>
          {info.partita_iva && <p>P.IVA {info.partita_iva}{info.codice_fiscale ? ` · CF ${info.codice_fiscale}` : ""}</p>}
          {(info.codice_sdi || info.pec) && <p>{info.codice_sdi ? `SDI ${info.codice_sdi}` : ""}{info.codice_sdi && info.pec ? " · " : ""}{info.pec ? `PEC ${info.pec}` : ""}</p>}
          {info.email_fattura && <p>Fatture a: {info.email_fattura}</p>}
        </div>
      )}
    </div>
  );
}
